import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';
import { PanelHostService } from '../../../../services/panel-host.service';
import { TripFinanceService } from '../../../../services/trip-finance.service';
import { TripService } from '../../../../services/trip.service';
import {
  ParticipantPaymentType,
  ParticipantPaymentWithTypeLabel,
} from '../../../../models/finance-models';
import { formatDateForInput } from '../../../trip-finances/trip-finance-date.util';
import { ContentContainerComponent } from '../../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-edit-participant-payment-panel',
  imports: [ReactiveFormsModule, TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './edit-participant-payment-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditParticipantPaymentPanelComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly fb = inject(FormBuilder);

  readonly paymentTypes = [
    { value: ParticipantPaymentType.Deposit, labelKey: 'deposit' },
    { value: ParticipantPaymentType.Payment, labelKey: 'payment' },
    { value: ParticipantPaymentType.Other, labelKey: 'other' },
  ];

  readonly form = this.fb.group({
    type: this.fb.control<ParticipantPaymentType>(ParticipantPaymentType.Payment, Validators.required),
    description: [''],
    due: ['', Validators.required],
    payment: [0, [Validators.required, Validators.min(0.01)]],
  });

  submitting = signal(false);
  isEdit = signal(false);
  recordId = signal<number | null>(null);

  constructor() {
    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.tripFinancePanelContext();
      if (panel === 'edit-participant-payment' && context?.mode === 'participant-payment') {
        this.isEdit.set(context.action === 'edit');
        if (context.action === 'edit' && context.record) {
          const record = context.record as ParticipantPaymentWithTypeLabel;
          this.recordId.set(record.id);
          this.form.patchValue({
            type: record.type,
            description: record.description,
            due: formatDateForInput(record.due),
            payment: record.payment,
          });
        } else {
          this.recordId.set(null);
          this.form.reset({
            type: ParticipantPaymentType.Payment,
            description: '',
            due: formatDateForInput(new Date()),
            payment: 0,
          });
        }
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  save(): void {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    const body = {
      type: this.form.value.type!,
      description: this.form.value.description ?? '',
      due: this.form.value.due!,
      payment: Number(this.form.value.payment),
    };

    this.submitting.set(true);
    const request$ =
      this.isEdit() && this.recordId()
        ? this.tripFinanceService.updateParticipantPayment(trip.id, this.recordId()!, body)
        : this.tripFinanceService.createParticipantPayment(trip.id, body);

    request$.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(
            marker(this.isEdit() ? 'trip-finances.panels.participant-payment.save-success-edit' : 'trip-finances.panels.participant-payment.save-success-add')
          ),
          duration: 4000,
          color: 'success',
        });
        await toast.present();
        this.panelHost.dismiss();
      },
      error: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(marker('trip-finances.panels.save-error')),
          duration: 4000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }
}
